import { IServerOptions, IUserSession } from "common/lib";
import { ProjectConnection } from "common/lib/gql";
import { ApolloGQLClient } from "./ApolloGQLClient";
import GQLUtils from "./GQLUtils";
import { GET_VIEWER } from "./queries";
import { UPDATE_VIEWER } from "./user/gql";
import { Group, License, Organization, ScenarioReleaseTypeEnum, UpdateUserMutationInput, User, UserWlcProfileSettings, Viewer } from "../../cms-glue/gql";
import { IProjectInfo } from "../../../interfaces/ScenarioElements";

export class GQLSession 
{
	static get SessionTypesMap(): { [index: string]: ScenarioReleaseTypeEnum }
	{
		return {
			"-2": ScenarioReleaseTypeEnum.NoSession,
			"-1": ScenarioReleaseTypeEnum.LocalSession,
			"0": ScenarioReleaseTypeEnum.PersistentSession,
			"1": ScenarioReleaseTypeEnum.PassiveSession,
			"2": ScenarioReleaseTypeEnum.DemoSession,
			"3": ScenarioReleaseTypeEnum.PersistentPerUser
		}
	}
    
	public static sessionTypeToGql(sessionType: number): ScenarioReleaseTypeEnum
	{
		return this.SessionTypesMap[sessionType.toString()];
	}
	
	public static gqlSessionTypeToInt(sessionType: ScenarioReleaseTypeEnum)
	{
		const sessionTypeInt = Object
			.keys(this.SessionTypesMap)
			.find((key: string) => this.SessionTypesMap[key] === sessionType)

		return parseInt(sessionTypeInt as string);
	}

	static get SessionTypesMapInverse(): { [index: string]: string }
	{
		return Object.entries(this.SessionTypesMap).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {});
	}

	public static async getViewer()
	{

		const data = await ApolloGQLClient.query(GET_VIEWER, undefined, 'request to get the viewer');

		console.info({
			data
		}, 'response to get the viewer');

		
		return data.viewer as Viewer;
	}

	public static async updateViewer(input: UpdateUserMutationInput)
	{
		const variables = {
			input
		};

		const data = await ApolloGQLClient.mutate(UPDATE_VIEWER, variables, 'request to update the viewer');

		const viewer: Viewer = data.updateUser.user;
		const errors = data.updateUser.errors;

		console.info({
			data
		}, 'response to update the viewer');

		return { viewer, errors };
	}
	
	public static async getSessionInfo()
	{
		let sessionInfo: IUserSession | null = null;
		const viewer = await this.getViewer();
		const errors: { message: string, code: number }[] = [];

		if (!viewer) 
		{
			errors.push({
				message: 'Unauthorized',
				code: 401
			})
			return { sessionInfo, errors };
		}

		const organization = viewer.organization as Organization;
		const scenarioRecipientGroups = (viewer.originGroup?.scenarioRecipientGroups?.nodes ?? []) as Group[];
		const wlcProfileSettings = viewer.wlcProfileSettings as UserWlcProfileSettings;

		// check for c2 access
		if (!organization?.projects?.permissions?.canIndexInCreate?.value) 
		{
			errors.push({
				message: 'Missing valid license',
				code: 403
			})
			return { sessionInfo, errors };
		}

		sessionInfo = {
			licenses: viewer.licenses?.nodes as License[],
			name: viewer.name as string,
			email: viewer.email as string,
			username: viewer.username as string,
			permalink: viewer.permalink as string,
			userId: wlcProfileSettings?.id as string,
			cmsUserId: viewer.databaseId as string,
			cmsUserGqlId: viewer.id as string,
			avatar: viewer.avatar?.fileUrl as string,
			companies: [
				{
					id: Number(organization?.databaseId),
					uuid: organization?.id,
					name: organization?.name as string,
					permalink: organization?.slug as string,
					user_origin_group: GQLUtils.gqlGroupToGroup(viewer.originGroup as Group),
					origin_scenario_recipient_groups: scenarioRecipientGroups.length > 0
						? scenarioRecipientGroups.map((g) => GQLUtils.gqlGroupToGroup(g))
						: [],
					settings: {
						features: organization?.settings?.features
					}
				}
			],
			accessToken: ApolloGQLClient.jwt,
			confirmed_eula: (wlcProfileSettings?.eulaVersionConfirmed || "") as string,
			release_notes_version_seen: wlcProfileSettings?.releaseNotesVersionSeen as string,
			project_list_seen: wlcProfileSettings?.projectListSeen as IProjectInfo[],
			creation_date: viewer.createdAt as string,
			wlc_settings: JSON.stringify(wlcProfileSettings?.wlcSettings),
			serverOptions: {
				CAD: wlcProfileSettings?.serverOptions?.cadProcessingService,
				upload_formats: wlcProfileSettings?.serverOptions?.uploadExtensions,
				flattenedConfig: wlcProfileSettings?.serverOptions?.flattenedConfig,
				cad_configuration: wlcProfileSettings?.serverOptions?.cadConfiguration
			} as IServerOptions,
			scope_admin: viewer.settings?.isScopeAdmin,
			projects: organization.projects as ProjectConnection
		};

		// Update async the last login date
		this.updateViewer({
			id: viewer.id,
			user: {
				wlcProfileSettings: {
					lastLogin: new Date().toISOString()
				}
			}
		});

		return { sessionInfo, errors };
	}
}